import { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserEdit } from '@/utils/permissions';
import { ClipboardCheck, Camera, Upload, X, CheckCircle, AlertCircle, Search, Eye, Plus, ChevronDown, ChevronUp, Minus, Loader2, Edit2, Package, Briefcase, FileText } from 'lucide-react';

// Searchable Dropdown Component
function SearchableDropdown({ 
  label, value, options, onChange, placeholder, displayField, valueField,
  required, disabled, testId, onSelect
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  const selectedOption = options.find(opt => opt[valueField] === value);
  const displayValue = selectedOption ? selectedOption[displayField] : '';
  
  const filteredOptions = options.filter(opt => 
    opt[displayField]?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-2.5 border rounded-xl text-sm text-left flex items-center justify-between transition ${
          disabled 
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
            : 'border-gray-200 hover:border-[#1a237e] focus:border-[#1a237e] focus:ring-2 focus:ring-blue-100'
        }`}
        data-testid={testId}
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search GRN..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">No GRNs pending QA</div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onChange(opt[valueField]);
                    if (onSelect) onSelect(opt);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition border-b border-gray-50 ${
                    opt[valueField] === value ? 'bg-blue-100 text-blue-800 font-medium' : 'text-gray-700'
                  }`}
                >
                  <div className="font-medium text-[#1a237e]">{opt[displayField]}</div>
                  <div className="text-xs text-gray-500">
                    {opt.material || opt.commodityName} - {opt.supplier || opt.vendorName}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function QA() {
  const { API, user } = useContext(AppContext);
  const canEdit = canUserEdit(user, '/stores/qa');
  const [qaList, setQaList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedQA, setExpandedQA] = useState(null);
  const [pendingGRNs, setPendingGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGRNs, setLoadingGRNs] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [capturedImages, setCapturedImages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Form state with support for multiple items
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [testType, setTestType] = useState('');
  const [qaItems, setQaItems] = useState([{
    material: '',
    batchNo: '',
    result: 'Pending',
    remarks: ''
  }]);

  // Edit QA Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQA, setEditingQA] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    remarks: '',
    purityLevel: '',
    moistureContent: ''
  });
  const [editFiles, setEditFiles] = useState([]);  // newly added files in Update modal
  const [updating, setUpdating] = useState(false);
  const [qaTab, setQaTab] = useState('material'); // 'material' or 'service'

  useEffect(() => {
    fetchQAList();
    fetchPendingGRNs();
  }, [qaTab]);

  const fetchQAList = async () => {
    try {
      // Production has hundreds of QAs — request up to 1000 in the flat-array
      // shape so the page paints quickly and reliably. Indexed on (date) so
      // sort is server-side and cheap.
      const response = await axios.get(`${API}/stores/qa`, { params: { poType: qaTab, limit: 1000 } });
      setQaList(response.data);
    } catch (error) {
      console.error('Error fetching QA list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingGRNs = async () => {
    setLoadingGRNs(true);
    try {
      const response = await axios.get(`${API}/stores/grns-pending-qa`, { params: { poType: qaTab } });
      setPendingGRNs(response.data || []);
    } catch (error) {
      console.error('Error fetching pending GRNs:', error);
    }
    setLoadingGRNs(false);
  };

  const handleGRNSelect = (grn) => {
    setSelectedGRN(grn);
    // Pre-fill items from GRN — one row per commodity. This matches the
    // backend's item-wise QA fan-out: each GRN item gets its own QA decision.
    const grnItems = Array.isArray(grn.items) && grn.items.length > 0 ? grn.items : null;
    if (grnItems) {
      setQaItems(grnItems.map(it => ({
        material: it.commodityName || it.material || '',
        batchNo: grn.invoiceNo || grn.invoiceNumber || '',
        testType: testType || '',  // seed from top-level default
        result: 'Pending',
        remarks: '',
      })));
    } else {
      setQaItems([{
        material: grn.material || grn.commodityName || '',
        batchNo: grn.invoiceNo || grn.invoiceNumber || '',
        testType: testType || '',
        result: 'Pending',
        remarks: '',
      }]);
    }
  };

  const addQAItem = () => {
    if (testType !== 'Visual Inspection') return;
    setQaItems(prev => [...prev, {
      material: '',
      batchNo: selectedGRN?.invoiceNo || selectedGRN?.invoiceNumber || '',
      result: 'Pending',
      remarks: ''
    }]);
  };

  const removeQAItem = (index) => {
    if (qaItems.length <= 1) return;
    setQaItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateQAItem = (index, field, value) => {
    setQaItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      alert('Unable to access camera.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsStreaming(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      setCapturedImages([...capturedImages, { data: canvas.toDataURL('image/jpeg', 0.9), name: `photo_${Date.now()}.jpg`, type: 'image/jpeg' }]);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { alert(`"${file.name}" exceeds 5MB limit.`); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        const entry = { data: reader.result, name: file.name, type: file.type, size: file.size };
        setCapturedImages(prev => [...prev, entry]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (index) => {
    setCapturedImages(capturedImages.filter((_, i) => i !== index));
  };

  const handleSubmitQA = async () => {
    if (!selectedGRN) return;
    // Validation: every item must have its own test type picked
    if (qaItems.some(it => !it.testType)) {
      alert('Please pick a Test Type for every inspection item.');
      return;
    }
    
    setSaveStatus('saving');
    
    try {
      // Submit QA records for each item to the backend
      for (const item of qaItems) {
        // Call backend to create QA record with result and commodity name.
        // Backend is idempotent — if a Pending auto-created QA already exists
        // for this (grnNo, commodity), the same id is returned.
        const response = await axios.post(`${API}/stores/qa`, null, {
          params: {
            grnNo: selectedGRN.id,
            inspector: user?.name || 'Current User',
            result: item.result || 'Pending',
            commodityName: item.material || ''
          },
          
        });
        
        // Trigger backend side-effects (lot creation, payment recompute,
        // auto-rejection) for BOTH Approved AND Rejected — POST alone
        // only updates the QA row, it doesn't run the QA-decision pipeline.
        if (item.result === 'Approved' || item.result === 'Rejected') {
          await axios.put(`${API}/stores/qa/${response.data.id}`, null, {
            params: {
              status: item.result,
              remarks: item.remarks
            },
            
          });
        }

        // Always persist the per-item testType + remarks + batchNo + images.
        await axios.put(`${API}/stores/qa/${response.data.id}/images`, {
          images: capturedImages,
          testType: item.testType,
          remarks: item.remarks,
          batchNo: item.batchNo,
        });
      }
      
      // Refresh QA list from backend
      await fetchQAList();
      
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setShowAddModal(false);
        resetForm();
        fetchPendingGRNs(); // Refresh pending GRNs
      }, 1500);
    } catch (error) {
      console.error('Error submitting QA:', error);
      // Show specific error message for duplicates
      const errorMsg = error.response?.data?.detail || 'Error submitting QA';
      alert(errorMsg);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const resetForm = () => {
    setSelectedGRN(null);
    setTestType('');
    setQaItems([{ material: '', batchNo: '', result: 'Pending', remarks: '' }]);
    setCapturedImages([]);
    stopCamera();
  };

  // Open edit modal for a QA record
  const openEditModal = (qa) => {
    setEditingQA(qa);
    setEditForm({
      status: qa.status || qa.result || 'Pending',
      remarks: qa.remarks || '',
      purityLevel: qa.purityLevel || '',
      moistureContent: qa.moistureContent || ''
    });
    setEditFiles([]);
    setShowEditModal(true);
  };

  // Handle QA status update
  const handleUpdateQA = async () => {
    if (!editingQA) return;
    
    setUpdating(true);
    try {
      // 1) Upload any newly attached files (append to existing)
      if (editFiles.length > 0) {
        const merged = [...(editingQA.images || []), ...editFiles];
        await axios.put(`${API}/stores/qa/${editingQA.id}/images`, {
          images: merged,
          remarks: editForm.remarks || '',
        });
      }

      // 2) Update status + fields
      const params = new URLSearchParams();
      params.append('status', editForm.status);
      if (editForm.remarks) params.append('remarks', editForm.remarks);
      if (editForm.purityLevel) params.append('purityLevel', editForm.purityLevel);
      if (editForm.moistureContent) params.append('moistureContent', editForm.moistureContent);
      
      await axios.put(`${API}/stores/qa/${editingQA.id}?${params.toString()}`);
      
      // Refresh QA list
      await fetchQAList();
      await fetchPendingGRNs();
      
      setShowEditModal(false);
      setEditingQA(null);
      setEditFiles([]);
      alert(`QA ${editingQA.id} updated to ${editForm.status}`);
    } catch (error) {
      console.error('Error updating QA:', error);
      alert(error.response?.data?.detail || 'Error updating QA status');
    } finally {
      setUpdating(false);
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'Approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'Rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'Pending': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'Approved': return <CheckCircle size={14} />;
      case 'Rejected': return <AlertCircle size={14} />;
      default: return null;
    }
  };

  // Check if multi-item is allowed (only for Visual Inspection)
  const canAddMultipleItems = testType === 'Visual Inspection';

  if (loading) return <div className="text-center py-12">Loading QA data...</div>;

  const stats = {
    total: qaList.length,
    approved: qaList.filter(q => (q.result || q.status) === 'Approved').length,
    rejected: qaList.filter(q => (q.result || q.status) === 'Rejected').length,
    pending: qaList.filter(q => (q.result || q.status) === 'Pending' || !(q.result || q.status)).length,
  };

  return (
    <div className="space-y-6" data-testid="qa-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quality Assurance (QA)</h1>
          <p className="text-gray-500 mt-1">Inspect materials and upload quality proof photos</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)} 
          className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md"
          data-testid="new-qa-btn"
        >
          <Plus size={18} /><span>New QA Check</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200"><p className="text-sm text-gray-500">Total Checks</p><p className="text-2xl font-bold text-gray-900">{stats.total}</p></div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2"><CheckCircle className="text-green-500" size={18} /><p className="text-sm text-green-600 font-medium">Approved</p></div>
          <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center space-x-2"><AlertCircle className="text-red-500" size={18} /><p className="text-sm text-red-600 font-medium">Rejected</p></div>
          <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <p className="text-sm text-orange-600 font-medium">Pending</p><p className="text-2xl font-bold text-orange-700">{stats.pending}</p>
        </div>
      </div>

      {/* Material QA / Service QA Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex">
          <button
            onClick={() => { setQaTab('material'); setShowAddModal(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              qaTab === 'material'
                ? 'border-amber-500 text-amber-700 bg-amber-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="material-qa-tab"
          >
            <Package size={18} />
            Material QA
          </button>
          <button
            onClick={() => { setQaTab('service'); setShowAddModal(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              qaTab === 'service'
                ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="service-qa-tab"
          >
            <Briefcase size={18} />
            Service QA
          </button>
        </div>
      </div>

      {/* Info Banner for Pending GRNs */}
      {pendingGRNs.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <ClipboardCheck className="text-blue-500" size={24} />
          <div>
            <p className="text-sm font-medium text-blue-800">{pendingGRNs.length} GRN(s) Pending QA</p>
            <p className="text-xs text-blue-600">Click "New QA Check" to start quality inspection</p>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by QA ID, GRN No, Material, Supplier, Inspector..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-[#1a237e] outline-none bg-white shadow-sm"
          data-testid="qa-search"
        />
      </div>

      {/* QA Summary Cards */}
      <div className="space-y-3">
        {qaList
          .filter(qa => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            return (
              qa.id?.toLowerCase().includes(term) ||
              qa.grnNo?.toLowerCase().includes(term) ||
              qa.material?.toLowerCase().includes(term) ||
              qa.supplier?.toLowerCase().includes(term) ||
              qa.inspector?.toLowerCase().includes(term) ||
              qa.invoiceNo?.toLowerCase().includes(term) ||
              qa.poNumber?.toLowerCase().includes(term)
            );
          })
          .map((qa) => (
          <div key={qa.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Card Header - Always visible */}
            <div
              className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-50/60 transition"
              onClick={() => setExpandedQA(expandedQA === qa.id ? null : qa.id)}
              data-testid={`qa-card-${qa.id}`}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getResultColor(qa.result || qa.status)}`}>
                    {getResultIcon(qa.result || qa.status)}<span>{qa.result || qa.status || 'Pending'}</span>
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-[#1a237e]">{qa.id}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{qa.material}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    <span>GRN: {qa.grnNo}</span>
                    <span>{qa.supplier || '-'}</span>
                    <span>{qa.date}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditModal(qa); }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100 transition"
                    title="Edit QA"
                  >
                    <Edit2 size={14} />
                    {(qa.result || qa.status) === 'Pending' ? 'Review' : 'Edit'}
                  </button>
                )}
                {expandedQA === qa.id ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </div>
            </div>

            {/* Expanded Details */}
            {expandedQA === qa.id && (
              <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/30 space-y-4">
                {/* Detail Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><p className="text-xs text-gray-500">QA ID</p><p className="text-sm font-medium text-gray-900">{qa.id}</p></div>
                  <div><p className="text-xs text-gray-500">GRN Number</p><p className="text-sm font-medium text-gray-900">{qa.grnNo}</p></div>
                  <div><p className="text-xs text-gray-500">PO Number</p><p className="text-sm font-medium text-gray-900">{qa.poNumber || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Invoice No</p><p className="text-sm font-medium text-gray-900">{qa.invoiceNo || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Material</p><p className="text-sm font-medium text-gray-900">{qa.material}</p></div>
                  <div><p className="text-xs text-gray-500">Supplier</p><p className="text-sm font-medium text-gray-900">{qa.supplier || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Quantity</p><p className="text-sm font-medium text-gray-900">{qa.quantity ? `${qa.quantity} ${qa.unit || 'kg'}` : '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Rate / Amount</p><p className="text-sm font-medium text-gray-900">{qa.rate ? `Rs. ${qa.rate}` : '-'} / {qa.amount ? `Rs. ${qa.amount}` : '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Inspector</p><p className="text-sm font-medium text-gray-900">{qa.inspector}</p></div>
                  <div><p className="text-xs text-gray-500">Date</p><p className="text-sm font-medium text-gray-900">{qa.date}</p></div>
                  <div><p className="text-xs text-gray-500">Test Type</p><p className="text-sm font-medium text-gray-900">{qa.testType || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Batch No</p><p className="text-sm font-medium text-gray-900">{qa.batchNo || '-'}</p></div>
                </div>

                {/* Remarks */}
                {qa.remarks && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Remarks</p>
                    <p className="text-sm text-gray-700">{qa.remarks}</p>
                  </div>
                )}

                {/* QA Images / Documents */}
                {qa.images?.length > 0 && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">QA Documents / Photos ({qa.images.length})</p>
                    <div className="flex flex-wrap gap-3">
                      {qa.images.map((img, idx) => {
                        const src = typeof img === 'string' ? img : img.data;
                        const name = typeof img === 'string' ? `Photo ${idx+1}` : (img.name || `File ${idx+1}`);
                        const isImage = typeof img === 'string' ? src.startsWith('data:image') : img.type?.startsWith('image/');
                        return isImage ? (
                          <a key={idx} href={src} target="_blank" rel="noreferrer"
                            className="block w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#1a237e] transition shadow-sm">
                            <img src={src} alt={name} className="w-full h-full object-cover" />
                          </a>
                        ) : (
                          <a key={idx} href={src} download={name}
                            className="flex flex-col items-center justify-center w-24 h-24 rounded-lg border-2 border-gray-200 hover:border-[#1a237e] transition shadow-sm bg-gray-50 p-2">
                            <FileText size={24} className="text-blue-500 mb-1" />
                            <span className="text-[9px] text-gray-500 text-center truncate w-full">{name}</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {qaList.length === 0 && (
          <div className="text-center py-12 text-gray-400">No QA records found</div>
        )}
      </div>

      {/* Add QA Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">New QA Check</h2>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>

            {/* GRN Selection (Dropdown) */}
            <div className="mb-6">
              <SearchableDropdown
                label="Select GRN (Pending QA)"
                value={selectedGRN?.id || ''}
                options={pendingGRNs}
                onChange={(value) => {
                  const grn = pendingGRNs.find(g => g.id === value);
                  if (grn) handleGRNSelect(grn);
                }}
                onSelect={handleGRNSelect}
                placeholder={loadingGRNs ? "Loading..." : "Select a GRN pending QA..."}
                displayField="id"
                valueField="id"
                required={true}
                testId="grn-dropdown"
              />
              {selectedGRN && (
                <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-500">Supplier:</span> <span className="font-medium">{selectedGRN.supplier || selectedGRN.vendorName}</span></div>
                    <div><span className="text-gray-500">Material:</span> <span className="font-medium">{selectedGRN.material || selectedGRN.commodityName}</span></div>
                    <div><span className="text-gray-500">Quantity:</span> <span className="font-medium">{selectedGRN.netWeight || selectedGRN.quantity} {selectedGRN.uom || selectedGRN.unit || 'kg'}</span></div>
                    <div><span className="text-gray-500">Date:</span> <span className="font-medium">{selectedGRN.date || selectedGRN.receivedDate}</span></div>
                  </div>
                </div>
              )}
            </div>

            {/* Default Test Type (quick-fill all items) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Test Type
                <span className="text-xs text-gray-400 font-normal ml-2">
                  (fills empty items below — each item can override)
                </span>
              </label>
              <select 
                value={testType} 
                onChange={(e) => {
                  const t = e.target.value;
                  setTestType(t);
                  // Apply to all items that don't have one set yet — convenient
                  // default for the common case where every item is the same test.
                  if (t) {
                    setQaItems(prev => prev.map(it => ({
                      ...it,
                      testType: it.testType || t,
                    })));
                  }
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                data-testid="test-type-select"
              >
                <option value="">— None (set each item below) —</option>
                <option value="Chemical Analysis">Chemical Analysis</option>
                <option value="Purity Test">Purity Test</option>
                <option value="Visual Inspection">Visual Inspection</option>
                <option value="Weight Verification">Weight Verification</option>
                <option value="Dimensional Check">Dimensional Check</option>
              </select>
              {selectedGRN && qaItems.length > 1 && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <CheckCircle size={12} /> Multi-item GRN — pick the test type for each commodity individually below
                </p>
              )}
            </div>

            {/* QA Items */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Inspection Items</h3>
                {selectedGRN && qaItems.length > 1 && (
                  <span className="text-xs text-gray-500">
                    {qaItems.length} items in GRN — decide each independently
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {qaItems.map((item, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-600">Item {index + 1}</span>
                      {qaItems.length > 1 && !(selectedGRN?.items?.length > 0) && (
                        <button
                          type="button"
                          onClick={() => removeQAItem(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Minus size={16} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Material *</label>
                        <input 
                          type="text" 
                          value={item.material} 
                          onChange={(e) => updateQAItem(index, 'material', e.target.value)} 
                          className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl ${selectedGRN?.items?.length > 0 ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          placeholder="Material name" 
                          readOnly={selectedGRN?.items?.length > 0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                        <input 
                          type="text" 
                          value={item.batchNo} 
                          onChange={(e) => updateQAItem(index, 'batchNo', e.target.value)} 
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50" 
                          placeholder="Auto-captured from GRN"
                          readOnly
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test Type *</label>
                        <select
                          value={item.testType || ''}
                          onChange={(e) => updateQAItem(index, 'testType', e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white"
                          data-testid={`item-${index}-test-type`}
                        >
                          <option value="">Select test type</option>
                          <option value="Chemical Analysis">Chemical Analysis</option>
                          <option value="Purity Test">Purity Test</option>
                          <option value="Visual Inspection">Visual Inspection</option>
                          <option value="Weight Verification">Weight Verification</option>
                          <option value="Dimensional Check">Dimensional Check</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Result</label>
                        <select 
                          value={item.result} 
                          onChange={(e) => updateQAItem(index, 'result', e.target.value)} 
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                        <input 
                          type="text" 
                          value={item.remarks} 
                          onChange={(e) => updateQAItem(index, 'remarks', e.target.value)} 
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" 
                          placeholder="Optional" 
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Camera Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2"><Camera size={18} className="text-[#1a237e]" /><span>Quality Proof Photos (Multiple)</span></h3>
              
              <div className="bg-gray-900 rounded-xl aspect-video relative overflow-hidden mb-4">
                {isStreaming ? <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                  : <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400"><Camera size={48} /><p className="mt-2 text-sm">Take photos of quality inspection</p></div>}
              </div>
              <canvas ref={canvasRef} className="hidden" />

              {/* Captured Images / Documents Grid */}
              {capturedImages.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {capturedImages.map((img, idx) => {
                    const src = typeof img === 'string' ? img : img.data;
                    const name = typeof img === 'string' ? `Photo ${idx+1}` : img.name;
                    const isImage = typeof img === 'string' || img.type?.startsWith('image/');
                    return (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                        {isImage ? (
                          <img src={src} alt={name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full p-2">
                            <FileText size={24} className="text-gray-400 mb-1" />
                            <span className="text-[10px] text-gray-500 text-center truncate w-full">{name}</span>
                          </div>
                        )}
                        <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"><X size={12} /></button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {!isStreaming ? (
                  <>
                    <button onClick={startCamera} className="flex-1 bg-[#1a237e] text-white py-2.5 px-4 rounded-xl font-medium flex items-center justify-center space-x-2"><Camera size={18} /><span>Start Camera</span></button>
                    <button onClick={() => fileInputRef.current?.click()} className="flex-1 bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-medium flex items-center justify-center space-x-2"><Upload size={18} /><span>Upload Files</span></button>
                    <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" multiple onChange={handleFileUpload} className="hidden" data-testid="qa-file-upload" />
                  </>
                ) : (
                  <>
                    <button onClick={capturePhoto} className="flex-1 bg-green-600 text-white py-2.5 px-4 rounded-xl font-medium">Capture Photo</button>
                    <button onClick={stopCamera} className="bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-medium">Done</button>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }} 
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitQA} 
                disabled={!selectedGRN || qaItems.some(item => !item.material || !item.testType) || saveStatus === 'saving'}
                className={`px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 ${
                  saveStatus === 'saved' 
                    ? 'bg-green-500 text-white' 
                    : saveStatus === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white disabled:opacity-50'
                }`}
                data-testid="submit-qa-btn"
              >
                {saveStatus === 'saving' ? (
                  <><Loader2 className="animate-spin" size={16} />Saving...</>
                ) : saveStatus === 'saved' ? (
                  <><CheckCircle size={16} />Saved!</>
                ) : saveStatus === 'error' ? (
                  <><AlertCircle size={16} />Error</>
                ) : (
                  <>Submit QA ({qaItems.length} item{qaItems.length > 1 ? 's' : ''})</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit QA Modal */}
      {showEditModal && editingQA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Update QA Status</h2>
              <button onClick={() => { setShowEditModal(false); setEditingQA(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* QA Info */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">QA ID:</span>
                  <span className="ml-2 font-medium text-[#1a237e]">{editingQA.id}</span>
                </div>
                <div>
                  <span className="text-gray-500">GRN:</span>
                  <span className="ml-2 font-medium">{editingQA.grnNo}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Material:</span>
                  <span className="ml-2 font-medium">{editingQA.material}</span>
                </div>
                <div>
                  <span className="text-gray-500">Quantity:</span>
                  <span className="ml-2 font-medium">{editingQA.quantity} {editingQA.unit || 'kg'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Current Status:</span>
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getResultColor(editingQA.status || editingQA.result)}`}>
                    {editingQA.status || editingQA.result || 'Pending'}
                  </span>
                </div>
              </div>
            </div>

            {/* Update Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Status *</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditForm({...editForm, status: 'Pending'})}
                    className={`px-4 py-3 rounded-xl text-sm font-medium border-2 transition ${
                      editForm.status === 'Pending' 
                        ? 'bg-orange-100 border-orange-400 text-orange-700' 
                        : 'border-gray-200 text-gray-600 hover:border-orange-300'
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm({...editForm, status: 'Approved'})}
                    className={`px-4 py-3 rounded-xl text-sm font-medium border-2 transition ${
                      editForm.status === 'Approved' 
                        ? 'bg-green-100 border-green-400 text-green-700' 
                        : 'border-gray-200 text-gray-600 hover:border-green-300'
                    }`}
                  >
                    ✓ Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm({...editForm, status: 'Rejected'})}
                    className={`px-4 py-3 rounded-xl text-sm font-medium border-2 transition ${
                      editForm.status === 'Rejected' 
                        ? 'bg-red-100 border-red-400 text-red-700' 
                        : 'border-gray-200 text-gray-600 hover:border-red-300'
                    }`}
                  >
                    ✗ Reject
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purity Level (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editForm.purityLevel}
                    onChange={(e) => setEditForm({...editForm, purityLevel: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm"
                    placeholder="e.g., 99.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Moisture (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editForm.moistureContent}
                    onChange={(e) => setEditForm({...editForm, moistureContent: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm"
                    placeholder="e.g., 0.5"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={editForm.remarks}
                  onChange={(e) => setEditForm({...editForm, remarks: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm"
                  rows={2}
                  placeholder="Add inspection notes..."
                />
              </div>

              {/* Attach File(s) — proof of QA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attach QA Proof <span className="text-gray-400 font-normal">(multiple formats)</span></label>
                <div className="border border-dashed border-gray-300 rounded-xl p-3 bg-gray-50">
                  {editFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {editFiles.map((f, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-md text-xs">
                          <FileText size={12} className="text-indigo-500" />
                          <span className="max-w-[160px] truncate" title={f.name}>{f.name}</span>
                          <span className="text-gray-400">({(f.size / 1024).toFixed(0)}KB)</span>
                          <button type="button" onClick={() => setEditFiles(prev => prev.filter((_, i) => i !== idx))} className="p-0.5 hover:bg-indigo-100 rounded-full">
                            <X size={10} className="text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-indigo-600 transition" data-testid="qa-edit-attach-label">
                    <Upload size={14} />
                    <span>Click to attach images, PDF, video, Excel, Word...</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                      data-testid="qa-edit-file-input"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach(file => {
                          if (file.size > 10 * 1024 * 1024) {
                            alert(`"${file.name}" exceeds 10MB limit.`); return;
                          }
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setEditFiles(prev => [...prev, {
                              name: file.name,
                              type: file.type,
                              size: file.size,
                              data: ev.target.result,
                              uploadedAt: new Date().toISOString(),
                            }]);
                          };
                          reader.readAsDataURL(file);
                        });
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {editingQA.images?.length > 0 && editFiles.length === 0 && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      {editingQA.images.length} existing file(s) — new files will be appended
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Info about approval */}
            {editForm.status === 'Approved' && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-700">
                  <CheckCircle size={14} className="inline mr-1" />
                  Approving will add this material to stock inventory and create a payment entry.
                </p>
              </div>
            )}

            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => { setShowEditModal(false); setEditingQA(null); setEditFiles([]); }}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateQA}
                disabled={updating || !editForm.status}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#1a237e] to-[#283593] text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {updating ? (
                  <><Loader2 className="animate-spin" size={16} />Updating...</>
                ) : (
                  <>Update Status</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QA;
